import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';

import presignS3 from '../../../utils/s3Presign';
import checkFileType from '../../../utils/checkFileType';
import addEditMamObject from '../../../api/graphql/mutations/addEditMamObject';
import {
  UPLOAD_KINDS, isAcceptedFile, uploadContentType, buildUploadName, readableName, previewUrl, formatBytes,
} from '../../../utils/radioAssets';
import { FieldErrors } from './RadioFields';

/*
An image or MP3 for a radio show: upload it, or paste a URL by hand.

The upload is the MAM uploader's flow (components/Views/Mam/Uploader.jsx) with three
differences the radio needs:
  - only the right files: .mp3 for the MP3, jpg/jpeg/png/gif for the image;
  - a real progress bar, because an episode MP3 is often 100 MB or more;
  - the MAM record is created with isPublic: true. ps-back sets the S3 object to
    public-read when it saves a public MAM object, and radio files must be public: the
    MP3 is the podcast enclosure and both are on the website.

The presigned URL comes from presignS3 (it adds the admin token /s3 requires), and the
PUT sends the same Content-Type the URL was signed for.

On success the field gets the https URL; onUploaded also gets the byte size, which the
form stores as fileSize for an MP3. A legacy value is protocol-relative
(//assets.primaverasound.com/...): it is previewed over https and never rewritten.
*/

const COPY = {
  image: { drop: 'Drop an image here, or click to choose one', types: 'jpg, jpeg, png or gif' },
  mp3: { drop: 'Drop an MP3 here, or click to choose one', types: 'mp3' },
};

const describeError = (error) => {
  const status = error && error.response && error.response.status;
  if (status === 401 || status === 403) return 'Your session has expired. Log in again and retry.';
  return (error && error.message) || 'The upload failed.';
};

const RadioFileUploader = ({
  kind, name, label, value, onChange, onUploaded, onBusyChange, help, error,
}) => {
  const [phase, setPhase] = useState(''); // '' | 'uploading' | 'registering'
  const [progress, setProgress] = useState({ loaded: 0, total: 0 });
  const [message, setMessage] = useState(null); // { type: 'ok' | 'error', text }
  const cancelRef = useRef(null);
  const onBusyRef = useRef(onBusyChange);

  useEffect(() => {
    onBusyRef.current = onBusyChange;
  }, [onBusyChange]);

  useEffect(() => {
    if (onBusyRef.current) onBusyRef.current(Boolean(phase));
  }, [phase]);

  // Closing the form mid-upload cancels the PUT instead of leaving it running.
  useEffect(() => () => {
    if (cancelRef.current) cancelRef.current.cancel('Upload cancelled.');
  }, []);

  const upload = async (file) => {
    if (!isAcceptedFile(kind, file.name)) {
      setMessage({ type: 'error', text: `Only ${COPY[kind].types} files here.` });
      return;
    }
    const {
      filename, extension, key, url,
    } = buildUploadName(file.name);
    const contentType = uploadContentType(kind, file);
    const source = axios.CancelToken.source();
    cancelRef.current = source;
    setMessage(null);
    setProgress({ loaded: 0, total: file.size });
    setPhase('uploading');

    try {
      const presigned = await presignS3(
        { fileName: key, fileType: contentType, type: 'upload' },
        { cancelToken: source.token },
      );
      const signedUrl = presigned && presigned.data;
      if (!signedUrl || typeof signedUrl !== 'string') throw new Error('Could not get an upload URL.');

      await axios.put(signedUrl, file, {
        headers: { 'Content-Type': contentType },
        cancelToken: source.token,
        onUploadProgress: (event) => {
          setProgress({ loaded: event.loaded, total: event.total || file.size });
        },
      });

      setPhase('registering');
      const mamObject = await addEditMamObject({
        assetSlugName: filename,
        filename,
        fileExtension: extension,
        url,
        fileReadableName: { en: readableName(file.name) },
        // checkFileType has no 'jpeg'; it is an image all the same.
        type: checkFileType(extension === 'jpeg' ? 'jpg' : extension),
        tags: [],
        isPublic: true,
        fileSize: file.size,
      });
      if (!mamObject) {
        throw new Error('The file was uploaded but could not be registered in MAM, so it is not public yet. Upload it again.');
      }

      onChange(url);
      if (onUploaded) onUploaded({ url, fileSize: file.size, fileName: file.name });
      setMessage(mamObject.isPublic === false
        ? { type: 'error', text: `Uploaded ${file.name}, but MAM reports it as private: check it in the MAM tab.` }
        : { type: 'ok', text: `Uploaded ${file.name} (${formatBytes(file.size)}).` });
    } catch (uploadError) {
      setMessage({
        type: 'error',
        text: axios.isCancel(uploadError) ? 'Upload cancelled.' : describeError(uploadError),
      });
    } finally {
      cancelRef.current = null;
      setPhase('');
    }
  };

  const busy = Boolean(phase);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: UPLOAD_KINDS[kind].accept,
    multiple: false,
    disabled: busy,
    onDropAccepted: (files) => {
      if (files[0]) upload(files[0]);
    },
    onDropRejected: () => setMessage({ type: 'error', text: `Only ${COPY[kind].types} files here.` }),
  });

  const percent = progress.total ? Math.min(100, Math.round((progress.loaded / progress.total) * 100)) : 0;
  const preview = previewUrl(value);

  return (
    <div className="radio-uploader w-full">
      <label className="flex flex-col font-americaMonoRegular text-xs uppercase" htmlFor={name}>
        {label}
        <input
          id={name}
          name={name}
          type="text"
          value={value || ''}
          placeholder="https://assets.primaverasound.com/..."
          disabled={busy}
          onChange={event => onChange(event.target.value)}
          className={`radio-input${[].concat(error || []).length ? ' has-error' : ''}`}
        />
      </label>
      <div
        {...getRootProps({
          className: `radio-dropzone font-americaMonoRegular${isDragActive ? ' is-active' : ''}${busy ? ' is-disabled' : ''}`,
        })}
      >
        <input {...getInputProps()} />
        {COPY[kind].drop}
        {' '}
        <span className="radio-help">{`(${COPY[kind].types})`}</span>
      </div>
      {busy ? (
        <div>
          <div className="radio-progress">
            <div className="radio-progress-bar" style={{ width: `${phase === 'registering' ? 100 : percent}%` }} />
          </div>
          <div className="radio-progress-line">
            <span>
              {phase === 'registering'
                ? 'Registering in MAM…'
                : `Uploading ${percent}% (${formatBytes(progress.loaded)} of ${formatBytes(progress.total)})`}
            </span>
            {phase === 'uploading' ? (
              <button type="button" className="radio-button is-ghost is-small" onClick={() => cancelRef.current && cancelRef.current.cancel('Upload cancelled.')}>
                Cancel
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      {message ? <p className={`radio-upload-message is-${message.type}`}>{message.text}</p> : null}
      {help ? <p className="radio-help">{help}</p> : null}
      <FieldErrors error={error} />
      {preview && kind === 'image' ? <img className="radio-preview-image" src={preview} alt="" /> : null}
      {preview && kind === 'mp3' ? (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio className="radio-preview-audio" controls preload="none" src={preview} />
      ) : null}
    </div>
  );
};

RadioFileUploader.propTypes = {
  kind: PropTypes.oneOf(['image', 'mp3']).isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onUploaded: PropTypes.func,
  onBusyChange: PropTypes.func,
  help: PropTypes.node,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
};

RadioFileUploader.defaultProps = {
  value: '',
  onUploaded: null,
  onBusyChange: null,
  help: null,
  error: null,
};

export default RadioFileUploader;
